import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, RotateCcw, SkipForward, SkipBack, X } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { GameBoard } from "@/components/GameBoard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

const EMPTY_BOARD = () => Array.from({ length: 6 }, () => Array.from({ length: 9 }, () => null));

// Scripted demo: a fake match between "Léa" and "Kofi"
const DEMO_MOVES = [
  {
    player: "Léa",
    tiles: [
      { row: 2, col: 3, letter: "M" }, { row: 2, col: 4, letter: "O" }, { row: 2, col: 5, letter: "T" }
    ],
    word: "MOT", score: 12,
    explanation_fr: "Léa ouvre la partie avec MOT, en passant par la case départ (Zone d'Émergence). Bonus mot doublé !",
    explanation_en: "Léa opens the game with MOT, crossing the center (Zone d'Émergence). Double word bonus!",
  },
  {
    player: "Kofi",
    tiles: [
      { row: 3, col: 5, letter: "R" }, { row: 4, col: 5, letter: "E" }
    ],
    word: "TRE", score: 5,
    explanation_fr: "Kofi construit TRE en s'appuyant sur le T de Léa. Le mot croise verticalement.",
    explanation_en: "Kofi extends downward from Léa's T to make TRE, using a vertical connection.",
  },
  {
    player: "Léa",
    tiles: [
      { row: 1, col: 4, letter: "S" }, { row: 0, col: 4, letter: "O" }
    ],
    word: "SOM", score: 8,
    explanation_fr: "Léa monte verticalement à travers SOM. La lettre O de MOT est réutilisée.",
    explanation_en: "Léa builds SOM vertically, reusing the O of MOT.",
  },
  {
    player: "Kofi",
    tiles: [
      { row: 2, col: 6, letter: "S" }
    ],
    word: "MOTS", score: 4,
    explanation_fr: "Kofi ajoute simplement un S pour former MOTS. Efficace et rapide.",
    explanation_en: "Kofi adds a single S to make MOTS. Quick and efficient.",
  },
];

export default function Demo() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [bonuses, setBonuses] = useState([]);
  const [plateau, setPlateau] = useState([]);
  const [board, setBoard] = useState(EMPTY_BOARD());
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [scores, setScores] = useState({ "Léa": 0, "Kofi": 0 });
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/games/bonuses");
        setBonuses(data.bonuses);
        setPlateau(data.plateau || []);
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    // recompute board + scores from step index
    const b = EMPTY_BOARD();
    const s = { "Léa": 0, "Kofi": 0 };
    for (let i = 0; i < step; i++) {
      const m = DEMO_MOVES[i];
      m.tiles.forEach((t) => { b[t.row][t.col] = t.letter; });
      s[m.player] += m.score;
    }
    setBoard(b);
    setScores(s);
  }, [step]);

  useEffect(() => {
    if (!playing) { clearTimeout(timerRef.current); return; }
    if (step >= DEMO_MOVES.length) { setPlaying(false); return; }
    timerRef.current = setTimeout(() => setStep((s) => s + 1), 3800);
    return () => clearTimeout(timerRef.current);
  }, [playing, step]);

  const currentMove = step > 0 ? DEMO_MOVES[step - 1] : null;
  const done = step >= DEMO_MOVES.length;

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("demo.badge")}</div>
            <h1 className="font-display font-extrabold text-4xl tracking-tight mt-1">{t("demo.title")}</h1>
          </div>
          <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="demo-close-btn">
            <X className="w-4 h-4 mr-1" /> {t("common.close")}
          </Button>
        </div>

        <div className="grid lg:grid-cols-[1fr_2fr] gap-6 items-start">
          <div className="space-y-4">
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("demo.progress")}</div>
                <div className="mt-1 text-3xl font-display font-black">{step} / {DEMO_MOVES.length}</div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {["Léa", "Kofi"].map((n) => (
                    <div key={n} className="p-3 rounded-lg bg-secondary text-center">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">{n}</div>
                      <div className="text-2xl font-display font-black">{scores[n]}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  {currentMove ? t("demo.lastMove") : t("demo.starting")}
                </div>
                {currentMove ? (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="secondary">{currentMove.player}</Badge>
                      <span className="font-mono font-bold">{currentMove.word}</span>
                      <Badge variant="default">+{currentMove.score}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed" data-testid="demo-explanation">
                      {lang === "fr" ? currentMove.explanation_fr : currentMove.explanation_en}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("demo.intro")}</p>
                )}
              </CardContent>
            </Card>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0} data-testid="demo-prev-btn">
                <SkipBack className="w-4 h-4 mr-1" /> {t("demo.back")}
              </Button>
              <Button size="sm" onClick={() => setPlaying(!playing)} data-testid="demo-toggle-btn"
                disabled={done}>
                {playing ? <><Pause className="w-4 h-4 mr-1" /> {t("demo.pause")}</> : <><Play className="w-4 h-4 mr-1" /> {t("demo.play")}</>}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setStep(Math.min(DEMO_MOVES.length, step + 1))}
                disabled={done} data-testid="demo-next-btn">
                <SkipForward className="w-4 h-4 mr-1" /> {t("demo.next")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setStep(0); setPlaying(true); }} data-testid="demo-restart-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> {t("demo.restart")}
              </Button>
            </div>

            {done && (
              <Card className="border-primary bg-primary/5">
                <CardContent className="p-6">
                  <h3 className="font-display font-bold text-lg mb-3">{t("demo.done.title")}</h3>
                  <div className="flex flex-col gap-2">
                    <Button onClick={() => { setStep(0); setPlaying(true); }} data-testid="demo-done-restart">
                      {t("demo.done.replay")}
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/tutorial")} data-testid="demo-done-tutorial">
                      {t("demo.done.tutorial")}
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/play")} data-testid="demo-done-play">
                      {t("demo.done.play")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="flex justify-center">
            <GameBoard board={board} bonuses={bonuses} plateau={plateau} size={48} />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
